import React, { useEffect, useRef, useState } from 'react'

import Checkbox from 'components/Atoms/Register/Checkbox'
import GroupSelect from 'components/Atoms/Register/GroupSelect'
import PasswordInput from 'components/Atoms/Register/PasswordInput'
import TextInput from 'components/Atoms/Register/TextInput'
import { useFetchAndLoad } from 'hooks'
import { CompanyData, RegisterFormData } from 'models/company.model'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { registerUser } from 'services/auth.service'

interface RegisterViewProps {
  companyData: CompanyData
  referralCode: string
  validationCode: string
}

export default function RegisterView({
  companyData,
  referralCode,
  validationCode,
}: RegisterViewProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('register')
  const { loading: registering, callEndpoint } = useFetchAndLoad()
  const accentColor = companyData.style.color_background

  const firstNameRef = useRef<HTMLInputElement>(null)
  const lastNameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const emailLocalPartRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const groupSelectRef = useRef<HTMLButtonElement>(null)

  const [formData, setFormData] = useState<RegisterFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    groupUuid: '',
    acceptTerms: false,
    acceptDataSharing: false,
    acceptNewsletter: false,
  })

  const [emailLocalPart, setEmailLocalPart] = useState('')
  const hasEmailDomain =
    companyData.email_domain && companyData.email_domain.trim() !== ''

  // Calcular el total de subgrupos disponibles
  const allSubgroups = companyData.groups.flatMap(g => g.subgroups)
  const hasSingleSubgroup = allSubgroups.length === 1
  const singleSubgroupUuid = hasSingleSubgroup ? allSubgroups[0].uuid : null

  // Auto-seleccionar el único subgrupo si solo hay uno
  useEffect(() => {
    if (hasSingleSubgroup && singleSubgroupUuid && !formData.groupUuid) {
      setFormData(prev => ({ ...prev, groupUuid: singleSubgroupUuid }))
    }
  }, [hasSingleSubgroup, singleSubgroupUuid])

  // Validaciones
  const isNameValid = formData.firstName.trim().length >= 3

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const isEmailValid = hasEmailDomain
    ? emailLocalPart.trim().length > 0
    : validateEmail(formData.email)

  const validatePassword = (password: string) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }
  }

  const passwordValidation = validatePassword(formData.password)
  const isPasswordValid =
    passwordValidation.minLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasNumber &&
    passwordValidation.hasSpecialChar

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isFormValid) {
      return
    }

    const finalEmail = hasEmailDomain
      ? `${emailLocalPart}@${companyData.email_domain}`
      : formData.email

    try {
      await callEndpoint(
        registerUser({
          email: finalEmail,
          first_name: formData.firstName,
          last_name: formData.lastName,
          password: formData.password,
          subgroup: formData.groupUuid,
          validation_code: validationCode,
          referral_code: referralCode,
        }),
      )

      navigate('/register-confirmation', {
        state: { accentColor },
      })
    } catch (error) {
      console.error('Error en registro:', error)
      toast.error(t('error.generic'), { theme: 'colored' })
    }
  }

  /* NOTE: Flujo de validación de email desactivado temporalmente.
   * Para reactivar, reemplazar handleSubmit por este código:
   *
   * const handleSubmitWithEmailValidation = async (e: React.FormEvent) => {
   *   e.preventDefault()
   *   if (!isFormValid) return
   *   const finalEmail = hasEmailDomain
   *     ? `${emailLocalPart}@${companyData.email_domain}`
   *     : formData.email
   *   try {
   *     const registrationData = {
   *       email: finalEmail,
   *       firstName: formData.firstName,
   *       lastName: formData.lastName,
   *       password: formData.password,
   *       groupUuid: formData.groupUuid,
   *       validationCode,
   *       referralCode,
   *       companyUuid: companyData.uuid,
   *       accentColor,
   *     }
   *     localStorage.setItem('pendingRegistration', JSON.stringify(registrationData))
   *     await callEndpoint(sendEmailValidation(finalEmail, companyData.uuid))
   *     toast.success(t('validation.sent'), { theme: 'colored' })
   *     setTimeout(() => navigate('/email-validation'), 1500)
   *   } catch (error) {
   *     console.error('Error al enviar validación de email:', error)
   *     toast.error(t('error.validation_send_failed'), { theme: 'colored' })
   *   }
   * }
   */

  const isFormValid =
    isNameValid &&
    isEmailValid &&
    isPasswordValid &&
    formData.groupUuid &&
    formData.acceptTerms &&
    formData.acceptDataSharing

  return (
    <div className="min-h-screen bg-gray-50 md:py-6">
      <div className="max-w-lg mx-auto w-full md:px-6">
        {/* Formulario con fondo blanco */}
        <div className="bg-white md:rounded-lg md:shadow-sm p-6 min-h-screen md:min-h-0">
          {/* Header con título y logo */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">{t('title')}</h1>
            <img
              src={companyData.style.banner}
              alt={companyData.name}
              className="h-10 object-contain"
            />
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <TextInput
              label={t('fields.firstName.label')}
              value={formData.firstName}
              onChange={value => setFormData({ ...formData, firstName: value })}
              required
              ref={firstNameRef}
              enterKeyHint="next"
              onKeyDown={e => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                lastNameRef.current?.focus()
              }}
            />
            {formData.firstName && !isNameValid && (
              <p className="text-red-500 text-xs mt-1 mb-3">
                {t('fields.firstName.error')}
              </p>
            )}

            <TextInput
              label={t('fields.lastName.label')}
              value={formData.lastName}
              onChange={value => setFormData({ ...formData, lastName: value })}
              ref={lastNameRef}
              enterKeyHint="next"
              onKeyDown={e => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                if (hasEmailDomain) {
                  emailLocalPartRef.current?.focus()
                } else {
                  emailRef.current?.focus()
                }
              }}
            />

            {hasEmailDomain ? (
              <div className="flex flex-col mb-4">
                <label className="font-semibold mb-2 text-sm">
                  {t('fields.email.label')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent overflow-hidden">
                  <input
                    type="text"
                    placeholder={t('fields.email.placeholder')}
                    value={emailLocalPart}
                    onChange={e =>
                      setEmailLocalPart(e.target.value.toLowerCase())
                    }
                    ref={emailLocalPartRef}
                    className="flex-1 min-w-0 p-3 rounded-l-lg outline-none lowercase"
                    autoCapitalize="off"
                    autoCorrect="off"
                    {...({ enterKeyHint: 'next' } as any)}
                    onKeyDown={e => {
                      if (e.key !== 'Enter') return
                      e.preventDefault()
                      passwordRef.current?.focus()
                    }}
                    required
                  />
                  <span className="px-2 py-3 text-gray-600 bg-gray-50 border-l border-gray-300 text-sm whitespace-nowrap flex-shrink-0">
                    @{companyData.email_domain}
                  </span>
                </div>
                {emailLocalPart && !isEmailValid && (
                  <p className="text-red-500 text-xs mt-1">
                    {t('fields.email.error')}
                  </p>
                )}
              </div>
            ) : (
              <>
                <TextInput
                  label={t('fields.email.label')}
                  type="email"
                  placeholder={t('fields.email.placeholderFull')}
                  value={formData.email}
                  onChange={value => setFormData({ ...formData, email: value })}
                  required
                  ref={emailRef}
                  enterKeyHint="next"
                  onKeyDown={e => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    passwordRef.current?.focus()
                  }}
                />
                {formData.email && !isEmailValid && (
                  <p className="text-red-500 text-xs mt-1 mb-3">
                    {t('fields.email.error')}
                  </p>
                )}
              </>
            )}

            <PasswordInput
              label={t('fields.password.label')}
              value={formData.password}
              onChange={value => setFormData({ ...formData, password: value })}
              required
              ref={passwordRef}
              enterKeyHint="next"
              onKeyDown={e => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                if (!hasSingleSubgroup) {
                  groupSelectRef.current?.focus()
                }
              }}
            />
            {formData.password && !isPasswordValid && (
              <div className="text-xs mt-1 mb-3 space-y-1">
                <p
                  className={
                    passwordValidation.minLength
                      ? 'text-green-600'
                      : 'text-red-500'
                  }
                >
                  {passwordValidation.minLength ? '✓' : '✗'}{' '}
                  {t('fields.password.validations.minLength')}
                </p>
                <p
                  className={
                    passwordValidation.hasUppercase
                      ? 'text-green-600'
                      : 'text-red-500'
                  }
                >
                  {passwordValidation.hasUppercase ? '✓' : '✗'}{' '}
                  {t('fields.password.validations.hasUppercase')}
                </p>
                <p
                  className={
                    passwordValidation.hasNumber
                      ? 'text-green-600'
                      : 'text-red-500'
                  }
                >
                  {passwordValidation.hasNumber ? '✓' : '✗'}{' '}
                  {t('fields.password.validations.hasNumber')}
                </p>
                <p
                  className={
                    passwordValidation.hasSpecialChar
                      ? 'text-green-600'
                      : 'text-red-500'
                  }
                >
                  {passwordValidation.hasSpecialChar ? '✓' : '✗'}{' '}
                  {t('fields.password.validations.hasSpecialChar')}
                </p>
              </div>
            )}

            {!hasSingleSubgroup && (
              <GroupSelect
                label={t('fields.group.label')}
                groups={companyData.groups}
                value={formData.groupUuid}
                onChange={value =>
                  setFormData({ ...formData, groupUuid: value })
                }
                placeholder={t('fields.group.placeholder')}
                required
                ref={groupSelectRef}
              />
            )}

            <div className="mt-6">
              <Checkbox
                checked={formData.acceptTerms}
                onChange={checked =>
                  setFormData({ ...formData, acceptTerms: checked })
                }
                accentColor={accentColor}
                required
                label={
                  <>
                    {t('checkboxes.acceptTerms.prefix')}{' '}
                    <span
                      style={{ color: accentColor }}
                      className="font-semibold"
                    >
                      {t('checkboxes.acceptTerms.link')}
                    </span>
                  </>
                }
              />

              <Checkbox
                checked={formData.acceptDataSharing}
                onChange={checked =>
                  setFormData({ ...formData, acceptDataSharing: checked })
                }
                accentColor={accentColor}
                required
                label={t('checkboxes.acceptDataSharing')}
              />

              <Checkbox
                checked={formData.acceptNewsletter}
                onChange={checked =>
                  setFormData({ ...formData, acceptNewsletter: checked })
                }
                accentColor={accentColor}
                label={t('checkboxes.acceptNewsletter')}
              />
            </div>

            <button
              type="submit"
              disabled={!isFormValid || registering}
              className="w-full py-4 rounded-full text-white font-semibold mt-6 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110"
              style={{
                backgroundColor: accentColor,
              }}
            >
              {registering ? t('button.registering') : t('button.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
